<script setup lang="ts">
const { $sentryClientSide } = useNuxtApp();
const { logout, user } = useOidcAuth();
const { getUserTokenBalance } = useUsages();
const config = useRuntimeConfig();
const toast = useToast();

const orgId = user.value?.userInfo?.org_id as string;
const userCanManageBilling =
  !orgId || (await allows(manageOrganizations, orgId));
const open = ref(false);
const tokenBalance = ref<null | number>(null);
const pluralizeCredit = computed(() =>
  tokenBalance.value && tokenBalance.value > 1 ? 'credits' : 'credit',
);
const isEnterpriseAccount = computed(() => {
  return user.value?.userInfo?.isEnterpriseAccount;
});

watch(open, async () => {
  if (isEnterpriseAccount.value) return;

  tokenBalance.value = null;
  if (open.value) {
    try {
      tokenBalance.value = await getUserTokenBalance();
    } catch (error) {
      toast.add({
        color: 'error',
        title: 'An error occurred while fetching your available credits.',
        description: 'Please try again later.',
        icon: 'i-lucide-triangle-alert',
      });

      $sentryClientSide.captureException(error);
    }
  }
});

const isCustomer = computed(() => {
  return !!user.value?.userInfo?.customerId;
});

const orgLogo = computed(() => {
  return user.value?.userInfo?.org_logo as string;
});

const customerPortalLink = computed(() => {
  if (user.value?.userInfo?.email) {
    return `${config.public.stripe.customer_portal_link}`.concat(
      `?prefilled_email=${encodeURIComponent(user.value.userInfo.email as string)}`,
    );
  }
  return config.public.stripe.customer_portal_link;
});

const { beforeEach } = useRouter();
beforeEach(() => {
  open.value = false;
});

// sentry feedback
const attachSentry = async () => {
  await nextTick();
  const widget = $sentryClientSide.getFeedback();
  widget?.attachTo(document.querySelector('#sentry-feedback-trigger'));
};
</script>

<template>
  <UPopover
    :content="{
      align: 'end',
    }"
    v-model:open="open"
    @click="open = false"
  >
    <UButton
      color="neutral"
      variant="ghost"
      icon="i-lucide-circle-user"
      size="xl"
      @click="attachSentry()"
      :data-ph-capture-attribute-id="`user-profile-menu:popover_${open ? 'close' : 'open'}`"
    >
      {{
      user?.userInfo?.nickname ??
        user?.userInfo?.name ??
        user?.userInfo?.email ??
        "Profile"
      }}
    </UButton>

    <template #content>
      <div class="p-4 flex flex-col gap-y-2 items-start w-[280px]">
        <template v-if="user?.userInfo?.org_name">
          <div
            class="flex gap-2 items-center px-5 p-2 cursor-pointer justify-around w-full"
            :class="{'bg-(--alpha-ui-bg-accented-50) rounded-md': isEnterpriseAccount}"
            @click="navigateTo(`/app/profile?tab=organizations`)"
            data-ph-capture-attribute-id="user-profile-menu:organization_click"
          >
            <span> Member of: </span>
            <NuxtImg
              v-if="orgLogo"
              :src="orgLogo"
              provider="cloudflare"
              height="75"
              width="100"
              fit="contain"
              class="max-h-[75px] object-contain"
            />
            <UIcon v-else name="i-lucide:building" class="size-12" />
          </div>
        </template>

        <template v-if="!isEnterpriseAccount">
          <div
            class="bg-(--alpha-ui-primary-10) rounded-md flex p-3 w-full justify-center align-center"
          >
            <div class="flex flex-col items-center gap-2">
              <USkeleton
                v-if="tokenBalance === null"
                class="bg-(--ui-bg-accented) text-center flex flex-col justify-center w-[180px] h-[32px]"
              />
              <span
                v-else
                class="text-(--ui-text-muted) text-center flex flex-col justify-center font-body-m-strong w-[180px] h-[32px]"
              >
                {{ tokenBalance }} {{ pluralizeCredit }} remaining</span
              >
              <USkeleton
                v-if="tokenBalance === null && userCanManageBilling"
                class="bg-(--ui-bg-accented) w-[127px] h-[36px]"
              />
              <BuyCreditButton
                v-else-if="userCanManageBilling"
                variant="soft"
                data-ph-capture-attribute-id="user-profile-menu:buy-credit-button_click"
              />
            </div>
          </div>
        </template>
        <UButton
          v-if="isCustomer && userCanManageBilling"
          variant="link"
          color="neutral"
          icon="i-lucide-external-link"
          trailing
          :ui="{ trailingIcon: 'size-4' }"
          :to="customerPortalLink"
          target="_blank"
          data-ph-capture-attribute-id="user-profile-menu:popover-tab-invoices_click"
        >
          Invoices & Infos
        </UButton>

        <UButton
          variant="link"
          color="neutral"
          to="/app/profile?tab=account"
          data-ph-capture-attribute-id="user-profile-menu:popover-tab-user-profile_click"
        >
          User profile
        </UButton>

        <UButton
          @click="open = false"
          id="sentry-feedback-trigger"
          variant="link"
          color="neutral"
          data-ph-capture-attribute-id="user-profile-menu:popover-tab-feedback_click"
        >
          Give feedback
        </UButton>

        <USeparator class="-mx-3.5 w-full-menu" />
        <Can :ability="manageInvoices">
          <UButton
            variant="link"
            color="neutral"
            to="/app/admin?tab=invoices"
            data-ph-capture-attribute-id="user-profile-menu:popover-tab-admin-tools_click"
          >
            Admin Tools
          </UButton>
        </Can>

        <UButton
          variant="link"
          color="neutral"
          @click="logout()"
          data-ph-capture-attribute-id="user-profile-menu:popover-tab-logout_click"
        >
          Sign out
        </UButton>

        <div
          class="flex flex-row justify-center items-center text-(--ui-text-muted) w-full-menu bg-(--ui-bg-elevated) py-1 rounded-b -mx-3.5 -mb-3.5"
        >
          <UButton
            :ui="{ label: 'font-caption-s text-(--ui-text-toned)' }"
            size="sm"
            color="neutral"
            variant="link"
            link
            label="Terms of Service"
            target="_blank"
            to="https://pro.cantook.com/en/access/termes-conditions"
            data-ph-capture-attribute-id="user-profile-menu:tos-button_click"
          />
          <div class="font-extrabold cursor-default">·</div>
          <UButton
            :ui="{ label: 'font-caption-s text-(--ui-text-toned)' }"
            size="sm"
            color="neutral"
            variant="link"
            link
            label="Privacy Policy"
            target="_blank"
            to="https://pro.cantook.com/en/access/politique-confidentialite"
            data-ph-capture-attribute-id="user-profile-menu:pp-button_click"
          />
        </div>
      </div>
    </template>
  </UPopover>
</template>

<style scoped>
.w-full-menu {
  width: calc(100% + 1.75rem);
}
</style>
